import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ExternalLink, X, MessageCircle } from 'lucide-react';
import { toEmbedUrl } from '../lib/youtube';
import { useSettings } from '../context/SettingsContext';

/**
 * Render the multi-section description so that paragraphs that look like
 * section headings (short, end in "?" or all-caps) are styled as headings.
 */
function FormattedDescription({ text }) {
  if (!text) return <span className="text-white/40">Course description coming soon.</span>;
  const blocks = text.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="space-y-3 text-sm leading-relaxed text-white/80">
      {blocks.map((b, i) => {
        const isHeading = looksLikeHeading(b);
        if (isHeading) {
          return (
            <h4 key={i} className="font-display text-base font-semibold text-white pt-2">
              {b}
            </h4>
          );
        }
        // Bullet list (lines starting with •)
        if (b.split('\n').every((line) => line.trim().startsWith('•'))) {
          return (
            <ul key={i} className="space-y-1.5 pl-1">
              {b.split('\n').map((line, j) => (
                <li key={j} className="flex gap-2">
                  <span className="text-[#c8e600] mt-0.5">•</span>
                  <span>{line.replace(/^•\s*/, '')}</span>
                </li>
              ))}
            </ul>
          );
        }
        return <p key={i}>{b}</p>;
      })}
    </div>
  );
}

function looksLikeHeading(text) {
  if (text.length > 110) return false;
  if (text.endsWith('?')) return true;
  if (text === text.toUpperCase() && text.length > 4 && text.length < 80) return true;
  return false;
}

export const CourseModal = ({ open, onOpenChange, course }) => {
  const { settings } = useSettings();
  if (!course) return null;
  const embed = toEmbedUrl(course.youtube_url || '');
  const waUrl = settings?.whatsapp_url || '';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="course-modal" className="max-w-5xl bg-[#07101f] border-white/12 text-white p-0 overflow-hidden max-h-[92vh]">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
          <div className="flex items-start gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-1.5">{course.agency} • {labelForCategory(course.category)}</div>
              <DialogTitle data-testid="course-modal-title" className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{course.name}</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        <div className="grid lg:grid-cols-5 gap-0 max-h-[calc(92vh-110px)]">
          {/* LEFT: Media + Description */}
          <div className="lg:col-span-3 p-6 overflow-y-auto scrollbar-thin">
            {embed ? (
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video">
                <iframe
                  data-testid="course-modal-youtube-embed"
                  src={embed}
                  title={course.name}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  frameBorder="0"
                />
              </div>
            ) : course.hero_image_url ? (
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0d223d] aspect-[21/9] relative">
                <img src={course.hero_image_url} alt={course.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0a1628]/60 via-transparent to-transparent" />
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/5 aspect-[21/9] flex items-center justify-center text-white/40 text-sm">Hero image coming soon</div>
            )}
            <div className="mt-5">
              <FormattedDescription text={course.description} />
            </div>
            {Array.isArray(course.gallery) && course.gallery.length > 0 && (
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#00b4d8] font-semibold mb-2">Photos</div>
                <div
                  data-testid="course-modal-gallery"
                  className="flex gap-2 overflow-x-auto overflow-y-hidden pb-2"
                  style={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorX: 'contain',
                    scrollSnapType: 'x proximity',
                    touchAction: 'pan-x pan-y',
                  }}
                >
                  {course.gallery.map((src, i) => (
                    <div
                      key={`${src}-${i}`}
                      className="relative shrink-0 rounded-xl overflow-hidden bg-[#0d223d] w-64 sm:w-72 aspect-[4/3]"
                      style={{ scrollSnapAlign: 'center' }}
                    >
                      <img
                        src={src}
                        alt={`${course.name} photo ${i + 1}`}
                        draggable={false}
                        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* RIGHT: Sticky CTA panel */}
          <div className="lg:col-span-2 p-6 border-t lg:border-t-0 lg:border-l border-white/10 flex flex-col bg-white/[0.02]">
            <div className="rounded-2xl border border-[#c8e600]/30 bg-[#c8e600]/[0.06] p-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#c8e600] font-semibold mb-1">Ready to dive in?</div>
              <div className="font-display text-lg font-semibold leading-snug">Register for {course.short_name || course.name}</div>
              <p className="mt-2 text-xs text-white/65">Click below and we'll get back to you with available dates, prerequisites and pricing.</p>
              {course.register_url ? (
                <a href={course.register_url} target="_blank" rel="noopener noreferrer">
                  <Button data-testid="course-modal-register-button" className="mt-4 w-full bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] font-semibold rounded-xl h-11">
                    Register Now <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              ) : (
                <Button disabled className="mt-4 w-full bg-white/10 text-white/40 rounded-xl h-11">Registration coming soon</Button>
              )}
              {waUrl && (
                <a href={waUrl} target="_blank" rel="noopener noreferrer" className="block mt-2">
                  <Button data-testid="course-modal-whatsapp-button" className="w-full bg-[#25D366] text-white hover:bg-[#1ebe57] font-semibold rounded-xl h-11">
                    <MessageCircle className="mr-2 h-4 w-4" /> Ask on WhatsApp
                  </Button>
                </a>
              )}
            </div>
            <div className="mt-4 text-xs text-white/55 space-y-1">
              <div className="flex items-center justify-between"><span>Agency</span><span className="text-white/80">{course.agency}</span></div>
              <div className="flex items-center justify-between"><span>Pathway</span><span className="text-white/80">{labelForCategory(course.category)}</span></div>
            </div>
            <Button data-testid="course-modal-close-button" variant="ghost" onClick={() => onOpenChange(false)} className="mt-auto text-white/65 hover:text-white hover:bg-white/5">
              <X className="mr-2 h-4 w-4" /> Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function labelForCategory(c) {
  const map = {
    entry: 'Entry Level',
    core: 'Core Pathway',
    professional: 'Professional',
    specialty: 'Specialty',
    open_circuit: 'Open Circuit',
    rebreather: 'Rebreather',
    overhead: 'Overhead Environment',
    service: 'Service',
  };
  return map[c] || c;
}
